import { useCallback, useEffect, useRef, useState } from "react";
import { fetchLiveActivitySnapshot } from "../../../services/liveActivity";

export const POLL_MS = 15000;

/** How often the live view refreshes: past an hour the chart doesn't change every 15
 * seconds, and re-reading a long range that often loads the database for nothing. A minute
 * keeps "who's online" fresh enough. */
export function pollMsFor(windowSec) {
  return windowSec <= 3600 ? POLL_MS : 60000;
}

/**
 * Snapshot state for the Live Activity modal. `at === null` means "live": poll
 * every pollMsFor(windowSec). Setting `at` to an ISO instant scrubs back in time and stops
 * polling until `setAt(null)`.
 */
export function useLiveActivity({ enabled, fetcher = fetchLiveActivitySnapshot }) {
  const [at, setAt] = useState(null);
  const [windowSec, setWindowSec] = useState(3600);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const reqId = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const id = ++reqId.current;
    setLoading(true);
    try {
      const snapshot = await fetcher({ at, windowSec });
      if (id !== reqId.current) return;
      setData(snapshot);
      setError(null);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      if (id !== reqId.current) return;
      setError(err?.message || String(err));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [enabled, fetcher, at, windowSec]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    if (at !== null) return undefined;
    const timer = setInterval(refresh, pollMsFor(windowSec));
    return () => clearInterval(timer);
  }, [enabled, at, windowSec, refresh]);

  return { data, loading, error, at, setAt, windowSec, setWindowSec, live: at === null, refresh, lastUpdated };
}
